import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ReportsService } from '../../../reports.service';
import {
  ColumnAggregation,
  DatasetColumn,
  DatasetConfig,
  DatasourceOption,
  isJoinFullyConfigured,
  mapApiColumn,
  SelectDatasourceSubmitPayload
} from './select-datasource-modal.model';
import { DatasetCardComponent } from './dataset-card/dataset-card.component';
import { MatIcon } from '@angular/material/icon';

let datasetSequence = 0;

@Component({
    selector: 'app-select-datasource-modal',
    templateUrl: './select-datasource-modal.component.html',
    styleUrl: './select-datasource-modal.component.scss',
    imports: [DatasetCardComponent, MatIcon]
})
export class SelectDatasourceModalComponent implements OnChanges {
  @Input() initialDatasets: DatasetConfig[] | null = null;

  @Output() apply = new EventEmitter<{ payload: SelectDatasourceSubmitPayload; response?: any }>();
  @Output() cancelled = new EventEmitter<void>();
  @Input() reportNameFromQueryParams!: string;
  datasets: DatasetConfig[] = [];
  datasourceOptions: DatasourceOption[] = [];
  isLoadingDatasources = false;
  isSubmitting = false;
  private datasourcesLoaded = false;

  constructor(private reportsService: ReportsService, private toastr: ToastrService) {
    this.datasets = [this.createDataset(true)];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialDatasets'] && this.initialDatasets?.length) {
      this.datasets = this.initialDatasets;
    }
  }

  // app-modal keeps this component mounted between opens, so the host must call this to clear stale state.
  resetDatasets(): void {
    this.datasets = this.initialDatasets?.length ? this.initialDatasets : [this.createDataset(true)];
    this.isSubmitting = false;
  }

  get joinCount(): number {
    return this.datasets.filter(dataset => !dataset.isPrimary).length;
  }

  get selectedColumnCount(): number {
    return this.datasets.reduce((total, dataset) => total + dataset.columns.filter(column => column.selected).length, 0);
  }

  get summaryText(): string {
    const datasetLabel = `${this.datasets.length} dataset${this.datasets.length === 1 ? '' : 's'}`;
    const columnLabel = `${this.selectedColumnCount} column${this.selectedColumnCount === 1 ? '' : 's'} selected`;
    const joinLabel = this.joinCount > 0 ? `${this.joinCount} join${this.joinCount === 1 ? '' : 's'} configured` : null;
    return [datasetLabel, columnLabel, joinLabel].filter(Boolean).join(' \u00b7 ');
  }

  get canApply(): boolean {
    if (!this.datasets.length || !this.datasets.every(dataset => !!dataset.name?.trim() && !!dataset.table_name)) {
      return false;
    }
    return this.datasets.every(dataset =>
      dataset.joins.every(join => isJoinFullyConfigured(join, dataset, this.datasets))
    );
  }

  // Guards "Add dataset" until the primary dataset has a name and a selected table.
  get canAddDataset(): boolean {
    const primary = this.datasets.find(dataset => dataset.isPrimary);
    return !!primary && !!primary.name?.trim() && !!primary.table_name;
  }

  ensureDatasourceOptionsLoaded(): void {
    if (this.datasourcesLoaded || this.isLoadingDatasources) {
      return;
    }
    this.loadDatasourceOptions();
  }

  trackByDatasetId(_: number, dataset: DatasetConfig): string {
    return dataset.id;
  }

  getUsedTableNames(current: DatasetConfig): string[] {
    return this.datasets
      .filter(dataset => dataset.id !== current.id && !!dataset.table_name)
      .map(dataset => dataset.table_name);
  }

  onAddDataset(): void {
    if (!this.canAddDataset) {
      return;
    }
    this.datasets.push(this.createDataset(false));
  }

  onRemoveDataset(dataset: DatasetConfig): void {
    this.datasets = this.datasets.filter(item => item.id !== dataset.id);
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onApply(): void {
    if (!this.canApply || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    // Datasets whose "manage columns" was never opened need their full column list fetched
    // so we can submit every column with the default aggregation instead of just the selected ones.
    const pendingDatasets = this.datasets.filter(dataset => !dataset.columnsLoaded);
    const columnFetches: Observable<any[]>[] = pendingDatasets.map(dataset =>
      this.reportsService.getDatasourceColumns({ type: 'columnInfo', tableName: dataset.table_name, datasetName: dataset.dataset_name })
    );

    (columnFetches.length ? forkJoin(columnFetches) : of([])).subscribe({
      next: (results: any[][]) => {
        pendingDatasets.forEach((dataset, index) => {
          dataset.columns = (results[index] || []).map(column => mapApiColumn(column));
        });
        this.submitDatasets();
      },
      error: () => {
        this.isSubmitting = false;
      }
    });
  }

  private submitDatasets(): void {
    const payload: SelectDatasourceSubmitPayload = {
      datasets: this.datasets.map(dataset => ({
        id: dataset.id,
        name: dataset.name,
        table_name: dataset.table_name,
        table_description: dataset.table_description,
        isPrimary: dataset.isPrimary,
        columns: this.buildColumnsPayload(dataset),
        joins: dataset.joins.map(join => ({
          targetDatasetId: join.targetDatasetId,
          joinType: join.joinType,
          columnMappings: join.columnMappings.map(mapping => ({
            leftColumn: mapping.leftColumn,
            rightColumn: mapping.rightColumn
          }))
        }))
      }))
    };
    const obj = {
      Report: this.reportNameFromQueryParams,
      DataSourceJson: JSON.stringify(payload)
    }

    this.reportsService.saveReportDatasourceConfig(obj).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        this.toastr.success('Data source configuration saved successfully', '', {
          timeOut: 5000,
          positionClass: 'toast-top-center'
        });
        // Only emit on success so the host only closes the modal when the save actually went through.
        this.apply.emit({ payload, response });
      },
      error: () => {
        this.isSubmitting = false;
        this.toastr.error('Failed to save data source configuration', '', {
          timeOut: 5000,
          positionClass: 'toast-top-center'
        });
      }
    });
  }

  // Send only the selected columns when any are selected; otherwise send every column, regardless of whether manage columns was opened.
  private buildColumnsPayload(dataset: DatasetConfig): Array<Pick<DatasetColumn, 'key' | 'name' | 'type' | 'aggregation'>> {
    const hasSelection = dataset.columns.some(column => column.selected);
    const columns = hasSelection ? dataset.columns.filter(column => column.selected) : dataset.columns;
    return columns.map(column => this.toColumnPayload(column));
  }

  private toColumnPayload(column: DatasetColumn): Pick<DatasetColumn, 'key' | 'name' | 'type' | 'aggregation'> {
    return { key: column.key, name: column.name, type: column.type, aggregation: column.aggregation as ColumnAggregation };
  }

  private loadDatasourceOptions(): void {
    this.isLoadingDatasources = true;
    const payload = {
      type: 'tableInfo'
    };
    this.reportsService.getReportDatasources(payload).subscribe({
      next: (options: DatasourceOption[]) => {
        this.datasourceOptions = options || [];
        this.isLoadingDatasources = false;
        this.datasourcesLoaded = true;
      },
      error: () => {
        this.isLoadingDatasources = false;
      }
    });
  }

  private createDataset(isPrimary: boolean): DatasetConfig {
    datasetSequence += 1;
    return {
      id: `dataset-${datasetSequence}`,
      name: '',
      dataset_name: '',
      table_name: '',
      table_description: '',
      isPrimary,
      manageColumnsOpen: false,
      columnsLoading: false,
      columnsLoaded: false,
      columns: [],
      relationshipsOpen: false,
      joins: []
    };
  }
}
